/**
 * The operational half of a chat copy pack (#2109 R3-B24): every string the
 * core used to carry inline — drawer chrome, bubble chrome, the message list,
 * the ended-session banner, the composer, dictation, attachments, export
 * labels, relative time — built from the `chat:core.*` catalog section.
 *
 * Both audiences share it. The companion pack passes the reader’s `t` and
 * locale; the staff pack passes a fixed-English `t` (staff surfaces stay
 * English until R4), so the two cannot drift apart on wording the core
 * renders the same way for both.
 *
 * The core stays literal-free AND key-free: it reads these slots through
 * `chatCopy()` and never names a key.
 */
import i18n from 'i18next';
import type { ChatCopyPack } from '@core/config/types';
import { formatPreset, formatRelativeCompact } from '@/i18n/intl';
import type { AnyTFunction } from '@/i18n/msg';

/** The loose call shape: the pack names full `chat:core.*` paths and passes params. */
export type PackT = (key: string, params?: Record<string, unknown>) => string;

export function asPackT(t: AnyTFunction): PackT {
  return t as unknown as PackT;
}

type OperationalSlots = Pick<
  ChatCopyPack,
  | 'locale'
  | 'bubbleChrome'
  | 'messageList'
  | 'sessionEnded'
  | 'composer'
  | 'typing'
  | 'errorBoundary'
  | 'dialog'
  | 'dictation'
  | 'attachments'
  | 'time'
  | 'systemMessage'
>;

/**
 * The slots the core reads that no audience voices differently — except the
 * empty-thread line, which names who the reader is writing to, so each pack
 * passes its own (D10: a family writes to their companion, staff to Williams).
 */
export function operationalCopy(
  t: PackT,
  locale: string,
  language: string,
  emptyBody: string,
): OperationalSlots {
  return {
    locale,
    bubbleChrome: {
      fromUser: t('chat:core.bubbleChrome.fromUser'),
      from: (name) => t('chat:core.bubbleChrome.from', { name }),
      status: {
        sending: t('chat:core.bubbleChrome.status.sending'),
        sent: t('chat:core.bubbleChrome.status.sent'),
        delivered: t('chat:core.bubbleChrome.status.delivered'),
        error: t('chat:core.bubbleChrome.status.error'),
      },
      thinkingToggle: t('chat:core.bubbleChrome.thinkingToggle'),
      thinkingLabel: t('chat:core.bubbleChrome.thinkingLabel'),
      attachment: {
        pdf: t('chat:core.bubbleChrome.attachment.pdf'),
        text: t('chat:core.bubbleChrome.attachment.text'),
        image: t('chat:core.bubbleChrome.attachment.image'),
      },
      flag: {
        idle: t('chat:core.bubbleChrome.flag.idle'),
        flagged: t('chat:core.bubbleChrome.flag.flagged'),
        failed: t('chat:core.bubbleChrome.flag.failed'),
        ariaLabel: t('chat:core.bubbleChrome.flag.ariaLabel'),
      },
      reactions: {
        regionLabel: t('chat:core.bubbleChrome.reactions.regionLabel'),
        good: t('chat:core.bubbleChrome.reactions.good'),
        bad: t('chat:core.bubbleChrome.reactions.bad'),
      },
    },
    messageList: {
      emptyTitle: t('chat:core.messageList.emptyTitle'),
      emptyBody,
      jumpToLatest: t('chat:core.messageList.jumpToLatest'),
      regionLabel: t('chat:core.messageList.regionLabel'),
      selectForRange: (position) => t('chat:core.messageList.selectForRange', { position }),
      unavailable: t('chat:core.messageList.unavailable'),
      historyLoadFailed: t('chat:core.messageList.historyLoadFailed'),
    },
    sessionEnded: {
      ended: t('chat:core.sessionEnded.ended'),
      voiceViewOnly: t('chat:core.sessionEnded.voiceViewOnly'),
      startNew: t('chat:core.sessionEnded.startNew'),
      resuming: t('chat:core.sessionEnded.resuming'),
      resume: t('chat:core.sessionEnded.resume'),
    },
    composer: {
      placeholder: t('chat:core.composer.placeholder'),
      ariaLabel: t('chat:core.composer.ariaLabel'),
      hint: t('chat:core.composer.hint'),
      send: t('chat:core.composer.send'),
      sending: t('chat:core.composer.sending'),
      logout: t('chat:core.composer.logout'),
    },
    typing: {
      label: t('chat:core.typing.label'),
      ariaLabel: (sender) => t('chat:core.typing.ariaLabel', { sender }),
    },
    errorBoundary: {
      title: t('chat:core.errorBoundary.title'),
      body: t('chat:core.errorBoundary.body'),
      retry: t('chat:core.errorBoundary.retry'),
      details: t('chat:core.errorBoundary.details'),
    },
    dialog: {
      close: t('chat:core.dialog.close'),
    },
    dictation: {
      transcribeFailed: t('chat:core.dictation.transcribeFailed'),
      transcribeFailedStatus: (status) => t('chat:core.dictation.transcribeFailedStatus', { status }),
      unsupported: t('chat:core.dictation.unsupported'),
      micDenied: t('chat:core.dictation.micDenied'),
      micFailed: t('chat:core.dictation.micFailed'),
    },
    attachments: {
      buttonTitle: t('chat:core.attachments.buttonTitle'),
      unsupported: t('chat:core.attachments.unsupported'),
    },
    time: {
      justNow: t('common:time.justNow'),
      ago: (count, unit) => formatRelativeCompact(-count, unit, { locale }),
      date: (value, shape, override) => formatPreset(value, shape, { locale: override ?? locale }),
    },
    // R3-B15: a client-built system line renders from `chat:system.<kind>`
    // (the page-level section; its keys are lane L5b’s). A kind with no key
    // returns null so the core shows the stored English `content` instead of
    // a raw key path.
    systemMessage: (kind, params) => {
      const key = `chat:system.${kind}`;
      return i18n.exists(key, { lng: language }) ? t(key, params) : null;
    },
  };
}

/** The drawer slots added by R3-B24 (the pre-existing title/groups/badges stay per audience). */
export function drawerChrome(t: PackT): Omit<ChatCopyPack['drawer'], 'title' | 'groups' | 'channelBadges' | 'untitledFallback'> {
  return {
    historyLabel: t('chat:core.drawer.historyLabel'),
    resizeLabel: t('chat:core.drawer.resizeLabel'),
    toolsLabel: t('chat:core.drawer.toolsLabel'),
    withHint: (label, hint) => t('chat:core.drawer.withHint', { label, hint }),
    newConversation: t('chat:core.drawer.newConversation'),
    searchConversations: t('chat:core.drawer.searchConversations'),
    viewOptions: t('chat:core.drawer.viewOptions'),
    viewOptionsHint: t('chat:core.drawer.viewOptionsHint'),
    download: t('chat:core.drawer.download'),
    downloadHint: t('chat:core.drawer.downloadHint'),
    downloadDisabledHint: t('chat:core.drawer.downloadDisabledHint'),
    endSession: t('chat:core.drawer.endSession'),
    endSessionDisabledHint: t('chat:core.drawer.endSessionDisabledHint'),
    recents: t('chat:core.drawer.recents'),
    loading: t('chat:core.drawer.loading'),
    loadFailed: t('chat:core.drawer.loadFailed'),
    retry: t('chat:core.drawer.retry'),
    empty: t('chat:core.drawer.empty'),
    viewAll: t('chat:core.drawer.viewAll'),
    rowActions: t('chat:core.drawer.rowActions'),
    exporting: t('chat:core.drawer.exporting'),
    exportDocx: t('chat:core.drawer.exportDocx'),
    exportPdf: t('chat:core.drawer.exportPdf'),
    delete: t('chat:core.drawer.delete'),
    deleteTitle: t('chat:core.drawer.deleteTitle'),
    deleteBody: t('chat:core.drawer.deleteBody'),
    cancel: t('chat:core.drawer.cancel'),
    deleting: t('chat:core.drawer.deleting'),
    rename: t('chat:core.drawer.rename'),
    renameTitle: t('chat:core.drawer.renameTitle'),
    renameLabel: t('chat:core.drawer.renameLabel'),
    renameSave: t('chat:core.drawer.renameSave'),
    renameSaving: t('chat:core.drawer.renameSaving'),
    renameFailed: t('chat:core.drawer.renameFailed'),
    clearSearch: t('chat:core.drawer.clearSearch'),
    noMatches: (query) => t('chat:core.drawer.noMatches', { query }),
    searchFooter: (count) => t('chat:core.drawer.searchFooter', { count }),
    recentsView: t('chat:core.drawer.recentsView'),
    views: { recent: t('chat:core.drawer.views.recent'), topics: t('chat:core.drawer.views.topics') },
    viewHints: { recent: t('chat:core.drawer.viewHints.recent'), topics: t('chat:core.drawer.viewHints.topics') },
    textSize: t('chat:core.drawer.textSize'),
    fontScales: {
      sm: t('chat:core.drawer.fontScales.sm'),
      md: t('chat:core.drawer.fontScales.md'),
      lg: t('chat:core.drawer.fontScales.lg'),
    },
    exportTitle: t('chat:core.drawer.exportTitle'),
    exportDescription: t('chat:core.drawer.exportDescription'),
    range: t('chat:core.drawer.range'),
    rangeLabel: t('chat:core.drawer.rangeLabel'),
    slices: {
      everything: t('chat:core.drawer.slices.everything'),
      today: t('chat:core.drawer.slices.today'),
      week: t('chat:core.drawer.slices.week'),
      custom: t('chat:core.drawer.slices.custom'),
    },
    customEmpty: t('chat:core.drawer.customEmpty'),
    rangeEmpty: t('chat:core.drawer.rangeEmpty'),
    downloadAs: t('chat:core.drawer.downloadAs'),
    formats: {
      markdown: t('chat:core.drawer.formats.markdown'),
      plainText: t('chat:core.drawer.formats.plainText'),
      word: t('chat:core.drawer.formats.word'),
      preparingWord: t('chat:core.drawer.formats.preparingWord'),
      pdf: t('chat:core.drawer.formats.pdf'),
      preparingPdf: t('chat:core.drawer.formats.preparingPdf'),
    },
    copied: t('chat:core.drawer.copied'),
    copyAll: t('chat:core.drawer.copyAll'),
    copyRange: t('chat:core.drawer.copyRange'),
    deleteThread: t('chat:core.drawer.deleteThread'),
    ungrouped: t('chat:core.drawer.ungrouped'),
  };
}

/** The untitled-row fallback: a locale-formatted date, plus the channel where marked (§33.2). */
export function untitledFallback(t: PackT, locale: string): ChatCopyPack['drawer']['untitledFallback'] {
  return (date, channelBadge) => {
    const label = formatPreset(date, 'monthDay', { locale });
    return channelBadge ? t('chat:core.drawer.untitledWithChannel', { date: label, channel: channelBadge }) : label;
  };
}

/** The export slots added by R3-B24 (the pre-existing titles stay per audience). */
export function exportChrome(
  t: PackT,
): Pick<
  ChatCopyPack['export'],
  'labels' | 'messageHeading' | 'unknownSession' | 'failed' | 'noMessagesInRange' | 'nothingToExport'
> {
  return {
    labels: {
      generated: t('chat:core.export.labels.generated'),
      totalMessages: t('chat:core.export.labels.totalMessages'),
      sessionId: t('chat:core.export.labels.sessionId'),
      exported: t('chat:core.export.labels.exported'),
      version: t('chat:core.export.labels.version'),
      from: t('chat:core.export.labels.from'),
      time: t('chat:core.export.labels.time'),
    },
    messageHeading: (position) => t('chat:core.export.messageHeading', { position }),
    unknownSession: t('chat:core.export.unknownSession'),
    failed: (status) => t('chat:core.export.failed', { status }),
    noMessagesInRange: t('chat:core.export.noMessagesInRange'),
    nothingToExport: t('chat:core.export.nothingToExport'),
  };
}
